import React, { useState, useEffect } from "react";
import {
  Box,
  Button,
  TextField,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  CircularProgress,
  Autocomplete,
  Grid,
  MenuItem,
} from "@mui/material";
import axiosInstance from "../../utils/axiosInstance";
import apiConfig from "../../config/appConfig";

const SalesforceConfigForm = ({
  open,
  onClose,
  initialValues = {},
  refreshConfigs,
  setSnackbar,
}) => {
  const [formValues, setFormValues] = useState({
    name: "",
    customerId: "",
    messagingApiConfigId: "",
    authURL: "",
    authClientId: "",
    authClientSecret: "",
    authAccountId: "",
    authGrantType: "password",
    deliveryAndEventCallbackDEId: "",
    transactionCallbackDEId: "",
    configType: "test",
    status: "Inactive",
    jwtSecret: "",
    restURL: ""
  });

  const [errors, setErrors] = useState({});
  const [customers, setCustomers] = useState([]);
  const [messagingApiConfigs, setMessagingApiConfigs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [customersLoading, setCustomersLoading] = useState(false);
  const [messagingConfigsLoading, setMessagingConfigsLoading] = useState(false);

  // Fetch customers and messaging API configs
  useEffect(() => {
    const fetchCustomers = async () => {
      setCustomersLoading(true);
      try {
        const response = await axiosInstance.get(
          apiConfig.endpoints.customers.base,
          {
            params: { status: "Active", limit: 10 },
          },
        );
        setCustomers(response.data.customers || []);
      } catch (error) {
        console.error("Error fetching customers:", error);
      } finally {
        setCustomersLoading(false);
      }
    };

    const fetchMessagingApiConfigs = async () => {
      setMessagingConfigsLoading(true);
      try {
        const response = await axiosInstance.get(
          apiConfig.endpoints.messagingApiConfig.base,
          {
            params: { status: "Active", limit: 10 },
          },
        );
        const data = Array.isArray(response.data.configs)
          ? response.data.configs
          : [];
        setMessagingApiConfigs(data);
      } catch (error) {
        console.error("Error fetching messaging API configs:", error);
        setMessagingApiConfigs([]);
      } finally {
        setMessagingConfigsLoading(false);
      }
    };

    fetchCustomers();
    fetchMessagingApiConfigs();
  }, []);

  // Set initial values when editing
  useEffect(() => {
    if (initialValues) {
      setFormValues({
        name: initialValues.name || "",
        customerId: initialValues.customerId || "",
        messagingApiConfigId: initialValues.messagingApiConfigId || "",
        authURL: initialValues.authURL || "",
        authClientId: initialValues.authClientId || "",
        authClientSecret: initialValues.authClientSecret || "",
        authAccountId: initialValues.authAccountId || "",
        authGrantType: initialValues.authGrantType || "password",
        deliveryAndEventCallbackDEId:
          initialValues.deliveryAndEventCallbackDEId || "",
        transactionCallbackDEId: initialValues.transactionCallbackDEId || "",
        configType: initialValues.configType || "test",
        status: initialValues.status || "Inactive",
        restURL: initialValues.restURL || "",
        jwtSecret: initialValues.jwtSecret || "",
      });
    }
  }, [initialValues]);

  // Handle input changes
  const handleInputChange = (event) => {
    const { name, value } = event.target;
    setFormValues((prev) => ({ ...prev, [name]: value }));
    setErrors((prev) => ({ ...prev, [name]: "" })); // Clear errors on change
  };

  // Validate fields
  const validateFields = () => {
    const validationErrors = {};
    if (!formValues.name.trim()) validationErrors.name = "Name is required";
    if (!formValues.customerId)
      validationErrors.customerId = "Customer is required";
    if (!formValues.messagingApiConfigId)
      validationErrors.messagingApiConfigId =
        "Messaging API Config is required";
    if (!formValues.authURL.trim())
      validationErrors.authURL = "Auth URL is required";

    if (!formValues.authClientId.trim())
      validationErrors.authClientId = "Auth Client ID is required";

    if (!formValues.authClientSecret.trim())
      validationErrors.authClientSecret = "Auth Client Secret is required";

    if (!formValues.authAccountId.trim())
      validationErrors.authAccountId = "Auth Account ID is required";

    if (!formValues.deliveryAndEventCallbackDEId.trim())
      validationErrors.deliveryAndEventCallbackDEId =
        "Delivery Callback DE ID is required";

    if (!formValues.transactionCallbackDEId.trim())
      validationErrors.transactionCallbackDEId =
        "Transaction Callback DE ID is required";

    if (!formValues.jwtSecret.trim())
      validationErrors.jwtSecret =
        "JWT Secret is required";

    if (!formValues.restURL.trim())
      validationErrors.restURL =
        "REST URL is required";    

    setErrors(validationErrors);
    return Object.keys(validationErrors).length === 0; // Return true if no errors
  };

  // Handle form submission
  const handleSubmit = async () => {
    if (!validateFields()) return;

    setLoading(true);
    try {
      const endpoint = initialValues.uuid
        ? apiConfig.endpoints.salesforceConfig.byId(
            initialValues.uuid,
          )
        : apiConfig.endpoints.salesforceConfig.base;

      const method = initialValues.uuid ? "put" : "post";
      await axiosInstance[method](endpoint, formValues);

      setSnackbar({
        open: true,
        message: initialValues.salesforceConfigId
          ? "Configuration updated successfully!"
          : "Configuration created successfully!",
        severity: "success",
      });

      refreshConfigs();
      onClose();
    } catch (error) {
      console.error("Error submitting configuration:", error);
      setSnackbar({
        open: true,
        message:
          error.response?.data?.message || "Failed to submit configuration.",
        severity: "error",
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="md">
      <DialogTitle>
        {initialValues.salesforceConfigId
          ? "Edit Configuration"
          : "Add Configuration"}
      </DialogTitle>
      <DialogContent>
        <Box sx={{ flexGrow: 1 }}>
          <Grid container spacing={2}>
            {/* First Row: Name */}

            {/* Second Row: Customer and Messaging API Config */}
            <Grid item xs={6}>
              <Autocomplete
                options={customers}
                getOptionLabel={(option) => option.name || ""}
                value={
                  customers.find(
                    (customer) => customer.customerId === formValues.customerId,
                  ) || null
                }
                onChange={(_, newValue) =>
                  setFormValues((prev) => ({
                    ...prev,
                    customerId: newValue?.customerId || "",
                  }))
                }
                loading={customersLoading}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Customer"
                    error={!!errors.customerId}
                    helperText={errors.customerId}
                    fullWidth
                    required
                  />
                )}
              />
            </Grid>
            <Grid item xs={6}>
              <Autocomplete
                options={messagingApiConfigs}
                getOptionLabel={(option) => option.name || ""}
                value={
                  messagingApiConfigs.find(
                    (config) =>
                      config.configId === formValues.messagingApiConfigId,
                  ) || null
                }
                onChange={(_, newValue) =>
                  setFormValues((prev) => ({
                    ...prev,
                    messagingApiConfigId: newValue?.configId || "",
                  }))
                }
                loading={messagingConfigsLoading}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Messaging API Config"
                    error={!!errors.messagingApiConfigId}
                    helperText={errors.messagingApiConfigId}
                    fullWidth
                    required
                  />
                )}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                label="Name"
                name="name"
                value={formValues.name}
                onChange={handleInputChange}
                error={!!errors.name}
                helperText={errors.name}
                fullWidth
                required
              />
            </Grid>
            {/* Third Row: Remaining Fields */}
            <Grid item xs={12}>
              <TextField
                label="Auth URL"
                name="authURL"
                value={formValues.authURL}
                onChange={handleInputChange}
                error={!!errors.authURL}
                helperText={errors.authURL}
                fullWidth
                required
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                label="REST API URL"
                name="restURL"
                value={formValues.restURL}
                onChange={handleInputChange}
                error={!!errors.restURL}
                helperText={errors.restURL}
                fullWidth
                required
              />
            </Grid>
            <Grid item xs={6}>
              <TextField
                label="Auth Grant Type"
                name="authGrantType"
                value={formValues.authGrantType}
                onChange={handleInputChange}
                select
                fullWidth
                required
              >
                <MenuItem value="password">Password</MenuItem>
                <MenuItem value="authorization_code">
                  Authorization Code
                </MenuItem>
                <MenuItem value="client_credentials">
                  Client Credentials
                </MenuItem>
              </TextField>
            </Grid>
            <Grid item xs={6}>
              <TextField
                label="Auth Client ID"
                name="authClientId"
                value={formValues.authClientId}
                onChange={handleInputChange}
                error={!!errors.authClientId}
                helperText={errors.authClientId}
                fullWidth
                required
              />
            </Grid>
            <Grid item xs={6}>
              <TextField
                label="Auth Client Secret"
                name="authClientSecret"
                value={formValues.authClientSecret}
                onChange={handleInputChange}
                error={!!errors.authClientSecret}
                helperText={errors.authClientSecret}
                fullWidth
                required
              />
            </Grid>
            <Grid item xs={6}>
              <TextField
                label="Auth Account ID"
                name="authAccountId"
                value={formValues.authAccountId}
                onChange={handleInputChange}
                error={!!errors.authAccountId}
                helperText={errors.authAccountId}
                fullWidth
                required
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                label="Salesforce JWT Secret"
                name="jwtSecret"
                value={formValues.jwtSecret}
                onChange={handleInputChange}
                error={!!errors.jwtSecret}
                helperText={errors.jwtSecret}
                fullWidth
                required
              />
            </Grid>
            <Grid item xs={6}>
              <TextField
                label="Delivery and Event Callback DE ID"
                name="deliveryAndEventCallbackDEId"
                value={formValues.deliveryAndEventCallbackDEId}
                onChange={handleInputChange}
                error={!!errors.deliveryAndEventCallbackDEId}
                helperText={errors.deliveryAndEventCallbackDEId}
                fullWidth
                required
              />
            </Grid>
            <Grid item xs={6}>
              <TextField
                label="Transaction Callback DE ID"
                name="transactionCallbackDEId"
                value={formValues.transactionCallbackDEId}
                onChange={handleInputChange}
                error={!!errors.transactionCallbackDEId}
                helperText={errors.transactionCallbackDEId}
                fullWidth
                required
              />
            </Grid>

            {/* Fourth Row: Config Type, Status, and Auth Grant Type */}
            <Grid item xs={6}>
              <TextField
                label="Config Type"
                name="configType"
                value={formValues.configType}
                onChange={handleInputChange}
                select
                fullWidth
                required
              >
                <MenuItem value="test">Test</MenuItem>
                <MenuItem value="development">Development</MenuItem>
                <MenuItem value="production">Production</MenuItem>
              </TextField>
            </Grid>
            <Grid item xs={6}>
              <TextField
                label="Status"
                name="status"
                value={formValues.status}
                onChange={handleInputChange}
                select
                fullWidth
                required
              >
                <MenuItem value="Active">Active</MenuItem>
                <MenuItem value="Inactive">Inactive</MenuItem>
              </TextField>
            </Grid>
          </Grid>
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} color="secondary" disabled={loading}>
          Cancel
        </Button>
        <Button
          onClick={handleSubmit}
          color="primary"
          variant="contained"
          disabled={loading}
        >
          {loading ? (
            <CircularProgress size={20} />
          ) : initialValues.salesforceConfigId ? (
            "Update"
          ) : (
            "Create"
          )}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default SalesforceConfigForm;
